import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminNav } from "@/components/admin/AdminNav";
import {
  addContactSuppression,
  getBriefPreview,
  getBriefSubscribers,
  getContactSuppressions,
  saveBriefOwnerNote,
  subscriberItems,
  suppressionItems,
  type BriefSubscriberStatus,
} from "@/lib/brief";
import { SEO } from "@/lib/seo";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";
import "@/styles/phase2-demand.css";

export default function AdminBrief() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(canberraDate());
  const [ownerNote, setOwnerNote] = useState("");
  const [subscriberFilter, setSubscriberFilter] = useState<BriefSubscriberStatus | "all">("all");
  const [suppressionEmail, setSuppressionEmail] = useState("");
  const [suppressionReason, setSuppressionReason] = useState("");

  const preview = useQuery({
    queryKey: ["admin-brief-preview", date],
    queryFn: () => getBriefPreview(date),
    retry: false,
  });
  const subscribers = useQuery({
    queryKey: ["admin-brief-subscribers"],
    queryFn: () => getBriefSubscribers(),
    retry: false,
  });
  const suppressions = useQuery({
    queryKey: ["admin-contact-suppressions"],
    queryFn: getContactSuppressions,
    retry: false,
  });

  useEffect(() => {
    setOwnerNote(preview.data?.ownerNote ?? "");
  }, [preview.data?.ownerNote]);

  const saveNote = useMutation({
    mutationFn: () => saveBriefOwnerNote(date, ownerNote.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-brief-preview", date] });
    },
  });
  const addSuppression = useMutation({
    mutationFn: () => addContactSuppression(suppressionEmail.trim(), suppressionReason.trim()),
    onSuccess: () => {
      setSuppressionEmail("");
      setSuppressionReason("");
      void queryClient.invalidateQueries({ queryKey: ["admin-contact-suppressions"] });
    },
  });

  const allSubscribers = subscriberItems(subscribers.data);
  const filteredSubscribers = subscriberFilter === "all"
    ? allSubscribers
    : allSubscribers.filter((subscriber) => subscriber.status === subscriberFilter);
  const counts = useMemo(
    () => ({
      pending: allSubscribers.filter((item) => item.status === "pending").length,
      confirmed: allSubscribers.filter((item) => item.status === "confirmed").length,
      unsubscribed: allSubscribers.filter((item) => item.status === "unsubscribed").length,
    }),
    [allSubscribers],
  );
  const suppressionList = suppressionItems(suppressions.data);

  const handleSuppression = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    addSuppression.mutate();
  };

  return (
    <div className="brand-a pb-admin-invites-page pb-admin-brief-page">
      <SEO
        title="Referral brief admin · PulseBoard"
        description="Preview and manage the Canberra Referral Brief."
        path="/admin/brief"
        noindex
      />
      <main className="pb-admin-invites-main">
        <AdminNav />

        <header className="pb-admin-heading">
          <div>
            <p>Admin · Weekly brief</p>
            <h1>Canberra Referral Brief</h1>
          </div>
          <label className="pb-admin-date-label">
            Issue date
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
        </header>

        <div className="pb-admin-brief-grid">
          <section aria-labelledby="brief-preview-heading">
            <div className="pb-admin-section-heading">
              <div>
                <h2 id="brief-preview-heading">Issue preview</h2>
                <p>Deterministic preview for the previous seven days.</p>
              </div>
            </div>
            {preview.isLoading ? <AdminState>Composing preview…</AdminState> : null}
            {preview.isError ? (
              <AdminState error>
                Preview unavailable. Check your admin access and try again.
                <button type="button" onClick={() => void preview.refetch()}>Retry</button>
              </AdminState>
            ) : null}
            {preview.data ? (
              <article className="pb-admin-brief-preview">
                <h3>{preview.data.subject}</h3>
                <div dangerouslySetInnerHTML={{ __html: preview.data.html }} />
                <details>
                  <summary>Plain-text version</summary>
                  <pre>{preview.data.text}</pre>
                </details>
              </article>
            ) : null}
          </section>

          <section aria-labelledby="owner-note-heading">
            <div className="pb-admin-section-heading">
              <div>
                <h2 id="owner-note-heading">For practice owners</h2>
                <p>Optional owner note sent only to subscribers who opted in.</p>
              </div>
            </div>
            <form className="pb-admin-note-form" onSubmit={(event) => {
              event.preventDefault();
              saveNote.mutate();
            }}>
              <textarea
                rows={8}
                maxLength={2000}
                value={ownerNote}
                onChange={(event) => setOwnerNote(event.target.value)}
                placeholder="Optional owner note about MBS, PSR, or running a practice…"
              />
              <button type="submit" disabled={saveNote.isPending}>
                {saveNote.isPending ? "Saving…" : "Save owner note"}
              </button>
              {saveNote.isSuccess ? <p role="status">Owner note saved.</p> : null}
              {saveNote.isError ? <p className="pb-demand-error" role="alert">Owner note could not be saved.</p> : null}
            </form>
          </section>
        </div>

        <section className="pb-admin-brief-section" aria-labelledby="subscribers-heading">
          <div className="pb-admin-section-heading pb-admin-section-heading--tabs">
            <div>
              <h2 id="subscribers-heading">Subscribers</h2>
              <p>{subscribers.data?.count ?? allSubscribers.length} total records.</p>
            </div>
            <div className="pb-admin-brief-counts" aria-label="Subscriber counts">
              <span>{counts.confirmed} confirmed</span>
              <span>{counts.pending} pending</span>
              <span>{counts.unsubscribed} unsubscribed</span>
            </div>
          </div>
          <label className="pb-admin-filter-label">
            Status
            <select
              value={subscriberFilter}
              onChange={(event) => setSubscriberFilter(event.target.value as BriefSubscriberStatus | "all")}
            >
              <option value="all">All</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="unsubscribed">Unsubscribed</option>
            </select>
          </label>
          {subscribers.isLoading ? <AdminState>Loading subscribers…</AdminState> : null}
          {subscribers.isError ? <AdminState error>Subscribers could not be loaded.</AdminState> : null}
          {!subscribers.isLoading && !subscribers.isError && filteredSubscribers.length === 0 ? (
            <AdminState>No subscribers match this status.</AdminState>
          ) : null}
          {filteredSubscribers.length > 0 ? (
            <div className="pb-admin-table-wrap">
              <table className="pb-admin-table pb-admin-brief-table">
                <thead><tr><th>Email</th><th>Role</th><th>Practice</th><th>Status</th><th>Confirmed</th><th>Last sent</th></tr></thead>
                <tbody>
                  {filteredSubscribers.map((subscriber) => (
                    <tr key={subscriber.id}>
                      <td>{subscriber.email}</td>
                      <td>{subscriber.role.replace("_", " ")}</td>
                      <td>{subscriber.practiceName ?? "—"}</td>
                      <td>{subscriber.status}</td>
                      <td>{formatAdminDate(subscriber.confirmedAt)}</td>
                      <td>{formatAdminDate(subscriber.lastSentAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <section className="pb-admin-brief-section" aria-labelledby="suppression-heading">
          <div className="pb-admin-section-heading">
            <div>
              <h2 id="suppression-heading">Contact suppression</h2>
              <p>Suppressed addresses receive neither brief nor verification email.</p>
            </div>
          </div>
          <form className="pb-admin-suppression-form" onSubmit={handleSuppression}>
            <label>Email<input type="email" required value={suppressionEmail} onChange={(event) => setSuppressionEmail(event.target.value)} /></label>
            <label>Reason<input required maxLength={500} value={suppressionReason} onChange={(event) => setSuppressionReason(event.target.value)} /></label>
            <button type="submit" disabled={addSuppression.isPending || !suppressionEmail.trim() || !suppressionReason.trim()}>
              {addSuppression.isPending ? "Adding…" : "Add suppression"}
            </button>
          </form>
          {addSuppression.isError ? <p className="pb-demand-error" role="alert">Suppression could not be added.</p> : null}
          {suppressions.isLoading ? <AdminState>Loading suppression list…</AdminState> : null}
          {suppressions.isError ? <AdminState error>Suppression list could not be loaded.</AdminState> : null}
          {suppressionList.length === 0 && !suppressions.isLoading && !suppressions.isError ? <AdminState>No suppressed addresses.</AdminState> : null}
          {suppressionList.length > 0 ? (
            <ul className="pb-admin-suppression-list">
              {suppressionList.map((item) => (
                <li key={item.email ?? item.emailLower}>
                  <strong>{item.email ?? item.emailLower}</strong>
                  <span>{item.reason}</span>
                  <time dateTime={item.createdAt}>{formatAdminDate(item.createdAt)}</time>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </main>
    </div>
  );
}

function AdminState({ children, error = false }: { children: React.ReactNode; error?: boolean }) {
  return <div className={`pb-admin-state${error ? " pb-admin-state--error" : ""}`} role={error ? "alert" : undefined}>{children}</div>;
}

function canberraDate(): string {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function formatAdminDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(date);
}
